import React, { useState } from "react";
import api from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Ask-AI panel (ChatGPT / OpenAI).
 * Defaults to the admin endpoint; pass endpoint="/askai/ask" for the public one.
 */
export default function AskAIPanel({
  endpoint = "/admin/ai/ask",
  defaultModel = "openai/gpt-4o-mini",
  showModelPicker = true,
  placeholder = "Ask a market question — e.g. 'Latest catalysts moving HDFC Bank this week'",
  testidPrefix = "askai",
}) {
  const [question, setQuestion] = useState("");
  const [model, setModel] = useState(defaultModel);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (question.trim().length < 3) return toast.error("Question is too short");
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(endpoint, showModelPicker ? { question, model } : { question });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ChatGPT request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 sm:p-6 space-y-4" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-2">
        <Bot className="w-4 h-4 text-[#D91E2A]" />
        <h3 className="font-heading font-bold text-lg text-slate-900">Ask AI</h3>
        <span className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500 ml-1">powered by ChatGPT</span>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder={placeholder}
          className="bg-slate-50 border-slate-200"
          data-testid={`${testidPrefix}-question`}
        />
        <div className="flex flex-wrap items-center gap-2">
          {showModelPicker && (
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="bg-slate-50 border-slate-200 w-52" data-testid={`${testidPrefix}-model`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai/gpt-4o-mini">GPT-4o mini (fast, cheap)</SelectItem>
                <SelectItem value="openai/gpt-4o">GPT-4o (deep)</SelectItem>
                <SelectItem value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</SelectItem>
                <SelectItem value="google/gemini-2.0-flash-001">Gemini 2.0 Flash</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button type="submit" disabled={busy || question.trim().length < 3} className="bg-[#D91E2A] hover:bg-[#B01820] text-white" data-testid={`${testidPrefix}-submit`}>
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Thinking…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Ask</>}
          </Button>
        </div>
      </form>

      {result && (
        <div className="pt-3 border-t border-slate-200 space-y-3" data-testid={`${testidPrefix}-result`}>
          <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">
            Answer · <span className="text-[#D91E2A]">{result.model}</span>
          </div>
          <div
            className="prose prose-slate max-w-none text-sm text-slate-800 prose-a:text-[#D91E2A] whitespace-pre-wrap"
            data-testid={`${testidPrefix}-answer`}
          >
            {result.answer}
          </div>
          <div className="text-[10px] text-slate-500 italic">
            AI-generated summary. Not investment advice — verify with a registered advisor before acting.
          </div>
        </div>
      )}
    </div>
  );
}
